/**
 * SSE consumer for the chat-first agent.
 *
 * The agent endpoint is POST `/api/analyses/<id>/chat/agent/` returning
 * `text/event-stream`. EventSource can't send a POST body, so we use
 * fetch() + ReadableStream and a small SSE-frame parser.
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

function getCsrfToken() {
  const row = document.cookie.split('; ').find(r => r.startsWith('csrftoken='));
  return row ? row.split('=')[1] : '';
}

function parseSSEFrame(frame) {
  let type = 'message';
  const dataLines = [];
  for (const line of frame.split('\n')) {
    if (line.startsWith('event: ')) type = line.slice(7).trim();
    else if (line.startsWith('data: ')) dataLines.push(line.slice(6));
    else if (line.startsWith('event:')) type = line.slice(6).trim();
    else if (line.startsWith('data:')) dataLines.push(line.slice(5));
  }
  if (dataLines.length === 0) return null;
  const raw = dataLines.join('\n');
  try {
    return { type, data: JSON.parse(raw) };
  } catch {
    return { type, data: raw };
  }
}

async function consumeSSE(body, onEvent) {
  const reader = body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let idx;
    while ((idx = buffer.indexOf('\n\n')) !== -1) {
      const frame = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 2);
      if (!frame.trim()) continue;
      const ev = parseSSEFrame(frame);
      if (ev) onEvent(ev.type, ev.data);
    }
  }
  // Flush trailing frame, if any.
  if (buffer.trim()) {
    const ev = parseSSEFrame(buffer);
    if (ev) onEvent(ev.type, ev.data);
  }
}

/**
 * Drive a single user turn. Resolves when the SSE stream closes
 * (message_complete or error). Aborts if `signal` is provided.
 */
export async function sendAgentTurn(analysisId, { text, attachedIds = [], autoStart = false } = {}, onEvent, { signal } = {}) {
  const r = await fetch(`${API_BASE_URL}/analyses/${analysisId}/chat/agent/`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRFToken': getCsrfToken(),
      'Accept': 'text/event-stream',
    },
    body: JSON.stringify({ message: text, attached_ids: attachedIds, auto_start: autoStart }),
    signal,
  });
  if (!r.ok) {
    let detail = `HTTP ${r.status}`;
    let code = null;
    let runningTurnId = null;
    try {
      const json = await r.json();
      detail = json.detail || detail;
      code = json.code || null;
      runningTurnId = json.turn_id || null;
    } catch { /* swallow */ }
    if (r.status === 409) {
      // A turn is already running server-side (double send, second tab, or a
      // send racing a still-running background turn). Not an error — the
      // caller attaches to the running turn via the /events/ channel.
      console.warn(`[qombra-sse] turn already in progress, attaching (turn_id=${runningTurnId})`);
      onEvent('turn_in_progress', { turn_id: runningTurnId, message: detail });
      return;
    }
    onEvent('error', { message: detail, code, status: r.status });
    return;
  }
  if (!r.body) {
    onEvent('error', { message: 'No response stream available.' });
    return;
  }
  try {
    await consumeSSE(r.body, onEvent);
  } catch (err) {
    // The user-initiated AbortController path is silent — caller already knows.
    if (err && err.name === 'AbortError') return;
    // Network/proxy dropped the long-lived SSE response. The agent turn keeps
    // running server-side (background runner); the durable /events/ channel
    // replays every missed event on reconnect. Signal the caller so it can
    // show a "reconnecting" affordance instead of treating this as a failure.
    console.warn(`[qombra-sse] agent stream disconnected: ${err?.message || err}`);
    onEvent('stream_disconnected', { message: err?.message || 'Stream interrupted' });
  }
}


/**
 * Signal the backend to stop the current agent turn.
 */
export async function stopAgentTurn(analysisId) {
  await fetch(`${API_BASE_URL}/analyses/${analysisId}/agent/stop/`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRFToken': getCsrfToken(),
    },
  });
}

/**
 * Subscribe to per-analysis out-of-band events (preprocessing progress,
 * background training/SHAP, etc.). Implemented by views_agent.analysis_events.
 *
 * Resilience: the native EventSource auto-reconnect can stall behind proxies
 * or when the tab is backgrounded. We supplement it with (a) a ping watchdog
 * that forces a reconnect if no ping arrives within PING_TIMEOUT_MS, and
 * (b) a visibilitychange listener that re-checks the connection when the
 * user comes back to the tab. The backend supports `?last_event_id=N` for
 * replay, so explicit reconnects resume from where we left off.
 */
export function subscribeAnalysisEvents(analysisId, onEvent, { initialLastEventId = 0 } = {}) {
  if (typeof window === 'undefined' || typeof EventSource === 'undefined') {
    return () => {};
  }
  const url = `${API_BASE_URL}/analyses/${analysisId}/events/`;
  const topics = [
    'preprocessing.progress',
    'preprocessing.complete',
    'artifact.update',
    'chat.message',
    'chat.message.update',
    // Turn events — dual-written to ChatEvent by the background turn runner
    // so a reconnecting client can replay a whole in-flight turn.
    'message_start',
    'assistant_message',
    'tool_message',
    'message_complete',
    'agent.status',
    'agent.phase_started',
    'agent.phase_completed',
    'agent.stopped',
    'agent.file_uploaded',
    'ping',
    'error',
  ];
  // Backend keepalive cadence is 15s; treat absence over ~3 intervals as stale.
  const PING_TIMEOUT_MS = 45_000;
  const WATCHDOG_INTERVAL_MS = 10_000;
  const BACKOFF_BASE_MS = 10_000;
  const BACKOFF_CAP_MS = 120_000;

  let es = null;
  // Start from the caller-provided cursor (the GET-history snapshot) so a
  // page load doesn't replay every past turn's events from id 0.
  let lastEventId = initialLastEventId || 0;
  let lastSeenAt = Date.now();
  let closed = false;
  let attempts = 0;
  let nextAttemptAt = 0;

  function attach() {
    if (closed) return;
    if (es) {
      try { es.close(); } catch { /* ignore */ }
    }
    attempts += 1;
    nextAttemptAt = Date.now() + Math.min(BACKOFF_BASE_MS * 2 ** (attempts - 1), BACKOFF_CAP_MS);
    if (attempts > 1) {
      console.warn(`[qombra-sse] events channel stale/closed, reconnecting (attempt ${attempts}, next retry in ${Math.round((nextAttemptAt - Date.now()) / 1000)}s)`);
    }
    // Pass the cursor explicitly so an explicit reconnect (not the browser's
    // internal one) still benefits from the backend's replay-from-id support.
    const u = lastEventId > 0 ? `${url}?last_event_id=${lastEventId}` : url;
    es = new EventSource(u, { withCredentials: true });
    lastSeenAt = Date.now();
    es.addEventListener('open', () => { lastSeenAt = Date.now(); });
    topics.forEach(topic => {
      es.addEventListener(topic, (e) => {
        lastSeenAt = Date.now();
        attempts = 0; // healthy again — reset the backoff
        if (e.lastEventId) {
          const n = parseInt(e.lastEventId, 10);
          if (!Number.isNaN(n) && n > lastEventId) lastEventId = n;
        }
        let data = e.data;
        try { data = JSON.parse(e.data); } catch { /* keep raw */ }
        onEvent(topic, data);
      });
    });
  }

  function reconnectIfStale() {
    if (closed) return;
    const stale = Date.now() - lastSeenAt > PING_TIMEOUT_MS;
    // Don't tear down a CONNECTING (0) EventSource unless it's ping-stale —
    // the browser's own reconnect may be mid-handshake.
    const needsAttach = !es || es.readyState === 2 || (es.readyState === 0 && stale) || (es.readyState === 1 && stale);
    if (!needsAttach) return;
    if (Date.now() < nextAttemptAt) return; // exponential backoff between forced reconnects
    attach();
  }

  function onVisibility() {
    if (document.visibilityState === 'visible') {
      // A returning tab reconnects immediately — skip any pending backoff.
      nextAttemptAt = 0;
      reconnectIfStale();
    }
  }

  attach();
  const watchdog = setInterval(reconnectIfStale, WATCHDOG_INTERVAL_MS);
  document.addEventListener('visibilitychange', onVisibility);

  return () => {
    closed = true;
    clearInterval(watchdog);
    document.removeEventListener('visibilitychange', onVisibility);
    if (es) {
      try { es.close(); } catch { /* ignore */ }
    }
  };
}
